import { FETCH_USER_SUCCESS, FETCH_USER_FAILURE } from '../modules/user';
import { updateSystemOverview } from '../modules/system';
import { logout } from '../modules/authentication';
import { friendlyPhone } from '../../utilities/phone';
import mixpanel from '../../utilities/mixpanel';
import { setRollbarUser } from '../../utilities/rollbar';
import { setAuthToken } from '../../providers/authTokenProvider';

const mapOpptyToRedux = oppty => {
  return {
    address: oppty.facility_address_line1,
    city: oppty.facility_address_city,
    state: oppty.facility_address_state,
    zip: oppty.facility_address_zip_code,
    country: oppty.facility_address_country,
    contactId: oppty.contact.contact_id,
    opptyId: oppty.oppty_id,
    contract: oppty.contract,
    serviceContractId: oppty.service_contract_id,
    prospectId: oppty.prospect_id,
    projectId: oppty.project_id,
    opptyName: oppty.name,
    sowId: oppty.project_sow_id,
    isCurrentOwner: oppty.contact.isCurrentOwner,
    isRelatedContact: oppty.contact.isRelatedContact,
    customerType: oppty.customer_type,
    isMultiFamilyHome: oppty.multi_family_home,
    salesRepInfo: oppty.sales_rep_info,
    productOffering: oppty.product_offering,
    hasAutoPay: oppty.hasAutoPay,
    eligibleForAutoPay: oppty.eligibleForAutoPay,
    ACHFee: oppty.ACHFee,
    ACHFeeType: oppty.ACHFeeType,
    autopayByCreditCardEligible: oppty.autopayByCreditCardEligible,
    childOpportunities: oppty.childOpportunities?.map(childOppty => mapOpptyToRedux(childOppty)),
  };
};

const translateUserResponse = store => next => action => {
  const handleGetUserSuccess = userAction => {
    const { payload } = userAction;
    // If nothing was returned, do not continue with shaping data
    if (!payload) {
      throw new Error('Get user returned no data');
    }

    // if they have a user but no active oppties
    // potentially the case if they were logged in before the BFF stripped out extra inactive oppties
    if (!payload.opportunitiesWithContracts || payload.opportunitiesWithContracts.length === 0) {
      store.dispatch(logout());
      return next(userAction);
    }

    if (payload.forceLogoutUser) {
      mixpanel.track('Forced log out');
      mixpanel.reset();
      store.dispatch(logout());
      return next(userAction);
    }

    // set new access and ID tokens
    if (payload.data?.accessToken) {
      setAuthToken(payload.data.accessToken);
    }

    // Configure rollbar to report them
    if (window.Rollbar && payload.emailAddress) {
      setRollbarUser(payload.emailAddress, friendlyPhone(payload.phoneNumber || payload.mobilePhoneNumber));
    }

    let opptyIndex = 0;
    if (store && store.getState().app) {
      const persistedContractId = store.getState().app.get('serviceContractId');
      const persistedProspectId = store.getState().app.get('defaultProspectId');

      // The persisted contract is from when the user changes their dropdown in the app
      // this allows them to come back to MSR with the same contract selected
      if (persistedContractId && !persistedProspectId) {
        opptyIndex = payload.opportunitiesWithContracts.findIndex(oppty => oppty.contract?.contractId === persistedContractId);
      }

      // This persisted prospect id is if the user followed a link that was meant to take them directly to a specific
      // account of theirs. This should take priority over the contract
      if (persistedProspectId) {
        opptyIndex = payload.opportunitiesWithContracts.findIndex(oppty => oppty.prospect_id === persistedProspectId);
      }

      // If they had an invalid one stored, just default back to 0
      opptyIndex = opptyIndex === -1 ? 0 : opptyIndex;
    }

    const initialAccount = payload.opportunitiesWithContracts[opptyIndex];

    const { contact, contract } = initialAccount;

    const opptys = payload.opportunitiesWithContracts.map(oppty => mapOpptyToRedux(oppty));

    const newAction = {
      type: userAction.type,
      payload: {
        customerNumber: payload.customerNumber,
        firstName: contact.first_name,
        lastName: contact.last_name,
        email: payload.emailAddress,
        phoneNumber: friendlyPhone(payload.phoneNumber),
        mobilePhoneNumber: friendlyPhone(payload.mobilePhoneNumber),
        address: initialAccount.facility_address_line1,
        city: initialAccount.facility_address_city,
        state: initialAccount.facility_address_state,
        zip: initialAccount.facility_address_zip_code,
        country: initialAccount.facility_address_country,
        contract,
        contactId: contact.contact_id,
        opptyId: initialAccount.oppty_id,
        prospectId: initialAccount.prospect_id,
        projectId: initialAccount.project_id,
        opptyName: initialAccount.name,
        isMultiFamilyHome: initialAccount.multi_family_home,
        sowId: initialAccount.project_sow_id,
        opportunities: opptys,
        masterContractInfo: payload.masterContractInfo,
      },
    };

    // if the system was loaded before the user info we need to ensure
    // the selected system overview matches the selected prospect on initial load
    store.dispatch(updateSystemOverview(initialAccount.prospect_id));
    return next(newAction);
  };

  const handleGetUserFailure = userAction => {
    const logEmOut = trackingMessage => {
      mixpanel.track(trackingMessage);
      mixpanel.reset();
      store.dispatch(logout());
      return next(userAction);
    };

    if (!userAction.payload.response && userAction.payload.message === 'Network Error') {
      // If there was a generic network error, log them out. This could have been caused
      // by tokens that were too long. This happens where there was NO response from the API
      // Success or Fail
      return logEmOut('Forced log out, Network Error detected');
    }

    if (userAction.payload?.response?.status === 404) {
      // if we did get a response back and it was a 404, this token is no longer valid
      // and they should be logged out
      return logEmOut('Forced log out, get user returned a 404');
    }

    return next(userAction);
  };

  switch (action.type) {
    case FETCH_USER_FAILURE:
      return handleGetUserFailure(action);
    case FETCH_USER_SUCCESS:
      return handleGetUserSuccess(action);
    default:
      return next(action);
  }
};

export default translateUserResponse;
